<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, fromUnixTime, isToday } from 'date-fns';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';
import { formatInboxWaitDuration } from 'dashboard/helper/inboxWaitDuration';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from 'dashboard/components-next/Conversation/ConversationCard/MessagePreview.vue';

const props = defineProps({
  item: { type: Object, required: true },
  origin: { type: Object, required: true },
  now: { type: Number, required: true },
  active: { type: Boolean, default: false },
});
const emit = defineEmits(['open']);
const { t } = useI18n();
const exactTimestamp = useExactTimestamp();
const timestamp = computed(() => {
  const date = fromUnixTime(props.item.timestamp);
  return format(date, isToday(date) ? 'HH:mm' : 'dd/MM HH:mm');
});
const assigneeInitials = computed(() => {
  const names = props.item.assignee?.name.trim().split(/\s+/) || [];
  return `${names[0]?.[0] || ''}${names.length > 1 ? names.at(-1)[0] : ''}`.toUpperCase();
});
const notificationLabel = computed(() => {
  const type = props.item.notification_type;
  return type
    ? t(`INBOX.TYPES_NEXT.${type.toUpperCase()}`)
    : t('INBOX_HIERARCHY.CONVERSATION');
});
const waitDuration = computed(() =>
  formatInboxWaitDuration(props.item.waiting_since, props.now, t)
);
</script>

<template>
  <button
    type="button"
    class="flex w-full min-w-0 flex-col gap-1 rounded-lg p-2 text-start text-n-slate-12 outline-none transition-colors hover:bg-n-alpha-1 focus-visible:ring-2 focus-visible:ring-n-brand"
    :class="active ? 'bg-n-teal-3 dark:bg-n-teal-3' : ''"
    :aria-label="`${item.contact.name} #${item.id}`"
    :aria-current="active ? 'true' : undefined"
    :data-conversation-id="item.id"
    @click="emit('open', item)"
  >
    <div class="flex w-full min-w-0 items-start justify-between gap-2">
      <span
        class="min-w-0 flex-1 whitespace-normal break-words text-sm font-medium"
      >
        {{ item.contact.name }}
      </span>
      <span
        :title="
          t('INBOX_HIERARCHY.LAST_CONTACT_MESSAGE', {
            time: exactTimestamp(item.timestamp),
          })
        "
        class="shrink-0 text-[0.65rem] text-n-slate-10"
      >
        {{ timestamp }}
      </span>
    </div>
    <div class="flex min-w-0 items-start gap-1 text-xs text-n-slate-11">
      <span class="shrink-0">#{{ item.id }} ·</span>
      <MessagePreview
        v-if="item.last_message"
        :message="item.last_message"
        :show-message-type="false"
        multi-line
        class="min-w-0 flex-1 [&_.text-body-main]:text-xs"
      />
      <span v-else class="min-w-0 break-words">{{
        t('INBOX_HIERARCHY.NO_CONTACT_MESSAGE')
      }}</span>
    </div>
    <div class="flex w-full min-w-0 items-center justify-between gap-2">
      <span
        :title="origin.label"
        class="flex min-w-0 items-center gap-1 text-xs text-n-brand-text"
      >
        <Icon :icon="origin.icon" class="size-3 shrink-0" />
        <span class="min-w-0 break-words">{{ notificationLabel }}</span>
      </span>
      <span
        v-if="item.unread_count"
        :title="
          t(
            item.assignee
              ? 'INBOX_HIERARCHY.UNREAD_BY_ASSIGNEE'
              : 'INBOX_HIERARCHY.UNREAD_UNASSIGNED'
          )
        "
        class="flex min-w-4 shrink-0 items-center justify-center rounded bg-n-teal-3 px-1 text-xs font-medium text-n-teal-11"
        data-unread-messages
      >
        {{ item.unread_count }}
      </span>
    </div>
    <div class="flex w-full min-w-0 flex-col items-start gap-1">
      <span
        v-if="item.waiting_since"
        :title="
          t('INBOX_HIERARCHY.WAITING_SINCE', {
            time: exactTimestamp(item.waiting_since),
          })
        "
        class="flex w-full min-w-0 items-start gap-1 text-xs text-n-amber-11"
        data-waiting-since
      >
        <Icon icon="i-lucide-clock-3" class="size-3 shrink-0" />
        <span class="min-w-0 break-words">
          {{ t('INBOX_HIERARCHY.WAITING_PREFIX') }}
          <span class="whitespace-nowrap">{{ waitDuration }}</span>
        </span>
      </span>
      <span
        v-if="item.assignee"
        :title="t('INBOX_HIERARCHY.ASSIGNEE', { name: item.assignee.name })"
        :aria-label="
          t('INBOX_HIERARCHY.ASSIGNEE', { name: item.assignee.name })
        "
        class="flex size-6 shrink-0 self-end items-center justify-center rounded-full bg-n-teal-3 text-[0.65rem] font-medium text-n-teal-11"
        data-assignee
      >
        {{ assigneeInitials }}
      </span>
      <span v-else class="self-end break-words text-[0.65rem] text-n-slate-10">
        {{ t('INBOX_HIERARCHY.NO_ASSIGNEE') }}
      </span>
    </div>
  </button>
</template>
